import React, { useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

export function FamilyWoven() {
  useEffect(() => {
    document.title = "FamilyWoven — The Family Uniform";
  }, []);

  return (
    <div className="min-h-screen bg-heritage-cream text-heritage-walnut font-sans">
      {/* ─── Navigation ─── */}
      <nav className="sticky top-0 z-50 bg-heritage-cream/80 backdrop-blur-md border-b border-heritage-linen px-6 py-4">
        <div className="container mx-auto flex items-center justify-between">
          <div className="font-serif text-2xl font-bold italic tracking-tight">FamilyWoven</div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-bold uppercase tracking-widest opacity-70">
            <a href="#" className="hover:text-heritage-sage transition-colors">Shop All</a>
            <a href="#" className="hover:text-heritage-sage transition-colors">Heritage</a>
            <a href="#" className="hover:text-heritage-sage transition-colors">Our Story</a>
          </div>

          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex">Log In</Button>
            <Button variant="heritage" size="md">Cart (0)</Button>
          </div>
        </div>
      </nav>

      {/* ─── Hero Section ─── */}
      <section className="relative px-6 py-20 lg:py-32 overflow-hidden">
        <div className="container mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="max-w-xl">
            <span className="inline-block px-4 py-1 rounded-full bg-heritage-sage/10 text-heritage-sage text-xs font-bold uppercase tracking-widest mb-6">
              Premium Personalized Apparel
            </span>
            <h1 className="font-serif text-6xl lg:text-8xl leading-[0.9] tracking-tight mb-8">
              The Family <br/><span className="italic font-light">Uniform.</span>
            </h1>
            <p className="text-lg lg:text-xl text-heritage-walnut/70 leading-relaxed mb-10">
              Your family is a brand with a history. We build high-quality, personalized basics that treat your name with the pride it deserves. Soft enough for the morning chaos, durable enough for the legacy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="heritage" size="xl">Start Your Bundle</Button>
              <Button variant="heritage-outline" size="xl">The Heritage Collection</Button>
            </div>
            <div className="mt-8 flex items-center gap-4 text-sm opacity-50">
               <div className="flex -space-x-2">
                 {[1,2,3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-heritage-cream bg-heritage-linen flex items-center justify-center text-[10px] font-bold">F{i}</div>)}
               </div>
               <span>Trusted by 12,000+ families since 2026</span>
            </div>
          </div>
          
          <div className="relative">
             <div className="aspect-[4/5] bg-heritage-linen rounded-[2rem] overflow-hidden shadow-2xl rotate-2 relative group transition-transform hover:rotate-0 duration-700">
                {/* Hero Image Placeholder */}
                <div className="absolute inset-0 bg-gradient-to-t from-heritage-walnut/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                   <div className="text-center p-12">
                      <p className="font-serif italic text-3xl opacity-20 mb-4 text-heritage-walnut">"Aged Linen & Morning Light"</p>
                      <div className="w-24 h-[1px] bg-heritage-walnut/10 mx-auto"></div>
                   </div>
                </div>
                {/* Floating Badge */}
                <div className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-heritage-linen">
                   <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest opacity-40 mb-1">Featured Product</p>
                        <h3 className="font-serif text-2xl">The Heritage Crewneck</h3>
                      </div>
                      <span className="font-mono text-sm font-bold text-heritage-rust">$58.00</span>
                   </div>
                </div>
             </div>
             {/* Background Shape */}
             <div className="absolute -z-10 top-20 -right-20 w-80 h-80 bg-heritage-sage/10 rounded-full blur-3xl"></div>
          </div>
        </div>
      </section>

      {/* ─── Featured Collections ─── */}
      <section className="py-24 bg-white px-6">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight">Built for every branch.</h2>
              <p className="mt-4 text-lg text-heritage-walnut/60">30+ products, 6 collections, one family identity.</p>
            </div>
            <Button variant="ghost">View All Collections</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'The Heritage Set', desc: 'Classic surnames & founding years.', color: 'bg-heritage-linen' },
              { title: 'Holiday Matching', desc: 'Personalized sets for the peak season.', color: 'bg-heritage-sage/10' },
              { title: 'Family Reunion', desc: 'Bulk sets for the big gathering.', color: 'bg-heritage-navy/5 text-heritage-navy' },
            ].map((col, i) => (
              <Card key={i} variant="outline" className="group cursor-pointer hover:border-heritage-sage hover:bg-heritage-cream transition-all duration-500">
                <div className={`aspect-video ${col.color} rounded-xl mb-8 overflow-hidden relative`}>
                   <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-heritage-walnut/5">
                      <span className="bg-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg">Browse Collection</span>
                   </div>
                </div>
                <h3 className="font-serif text-3xl mb-2">{col.title}</h3>
                <p className="text-heritage-walnut/50">{col.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-24 bg-heritage-cream px-6 border-y border-heritage-linen">
        <div className="container mx-auto text-center">
          <h2 className="font-serif text-4xl lg:text-5xl mb-16">Simple as a family Sunday.</h2>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { step: '01', title: 'Choose Your Set', text: 'Select styles for every member, from toddlers to grandparents.' },
              { step: '02', title: 'Personalize It', text: 'Add names and years. Our engine handles the perfect alignment.' },
              { step: '03', title: 'Legacy Delivered', text: 'Printed on demand and delivered to your porch in 7 days.' },
            ].map((step, i) => (
              <div key={i} className="space-y-6">
                <div className="text-heritage-rust font-serif italic text-2xl">{step.step}</div>
                <h4 className="text-xl font-bold uppercase tracking-widest">{step.title}</h4>
                <p className="text-heritage-walnut/60 leading-relaxed max-w-xs mx-auto">{step.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-20">
             <Button variant="heritage" size="xl">Create Your Family Brand</Button>
          </div>
        </div>
      </section>

      {/* ─── Social Proof / Narrative ─── */}
      <section className="py-32 px-6 bg-heritage-linen/30">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-serif text-3xl lg:text-4xl italic text-heritage-walnut/80 leading-relaxed">
              "Matching apparel used to feel like a costume. FamilyWoven made it feel like we belong to a unit. It’s the first thing we pack for the cabin."
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-full bg-heritage-sage/20"></div>
              <div className="text-left">
                <p className="font-bold text-sm uppercase tracking-widest">The Miller Family</p>
                <p className="text-xs opacity-50">Austin, Texas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Newsletter ─── */}
      <section className="py-24 px-6">
        <div className="container mx-auto max-w-3xl">
          <Card variant="linen" className="text-center space-y-8">
            <h2 className="font-serif text-4xl italic text-heritage-walnut">Join the inner circle.</h2>
            <p className="text-heritage-walnut/60 max-w-md mx-auto">
              Get the latest collection drops and 10% off your first family order. No spam, just heritage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <Input placeholder="Your family email" className="bg-white" />
              <Button variant="heritage" className="whitespace-nowrap">Join Now</Button>
            </div>
          </Card>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-20 bg-heritage-walnut text-heritage-cream px-6">
        <div className="container mx-auto grid md:grid-cols-4 gap-12 border-b border-white/10 pb-20">
          <div className="space-y-6">
            <div className="font-serif text-3xl italic tracking-tight">FamilyWoven</div>
            <p className="text-sm opacity-60 leading-relaxed">
              Premium personalized apparel for families who want to feel like a unit. Built on demand, delivered with pride.
            </p>
          </div>
          <div>
            <h4 className="font-bold text-xs uppercase tracking-[0.2em] mb-6">Shop</h4>
            <ul className="space-y-3 text-sm opacity-60">
              <li><a href="#" className="hover:opacity-100 transition-opacity">Heritage Collection</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">Holiday Matching</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">New Arrivals</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">Gift Cards</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-xs uppercase tracking-[0.2em] mb-6">Company</h4>
            <ul className="space-y-3 text-sm opacity-60">
              <li><a href="#" className="hover:opacity-100 transition-opacity">Our Story</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">Sustainability</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">Affiliates</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">Contact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-xs uppercase tracking-[0.2em] mb-6">Support</h4>
            <ul className="space-y-3 text-sm opacity-60">
              <li><a href="#" className="hover:opacity-100 transition-opacity">How It Works</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">Returns & Exchanges</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">Shipping Info</a></li>
              <li><a href="#" className="hover:opacity-100 transition-opacity">FAQ</a></li>
            </ul>
          </div>
        </div>
        <div className="container mx-auto pt-10 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] uppercase tracking-widest opacity-30">
          <p>© 2026 FamilyWoven Inc. All Rights Reserved.</p>
          <div className="flex gap-8">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
